'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { useCart } from '@/contexts/CartContext';

interface NavigationItem {
  label: string;
  href: string;
  icon: string;
}

interface NavigationBarProps {
  className?: string;
}

const NavigationBar = ({ className = '' }: NavigationBarProps) => {
  const pathname = usePathname();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { getTotalItems } = useCart();
  const totalItems = getTotalItems();

  const navigationItems: NavigationItem[] = [
    { label: 'Home', href: '/home-landing', icon: 'HomeIcon' },
    { label: 'Menu', href: '/menu-catalog', icon: 'Squares2X2Icon' },
    { label: 'About', href: '/about-restaurant', icon: 'InformationCircleIcon' },
    { label: 'Gallery', href: '/food-gallery', icon: 'PhotoIcon' },
    { label: 'Contact', href: '/contact-us', icon: 'PhoneIcon' },
  ];

  const notificationsItem: NavigationItem = {
    label: 'Offers & Notifications',
    href: '/notifications',
    icon: 'BellIcon',
  };

  const isActive = (href: string) => pathname === href;

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <nav
        className={`sticky top-0 z-100 bg-card shadow-md transition-smooth ${className}`}
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-20 items-center justify-between gap-4">
            {/* Logo + Nav links grouped (links shifted right towards logo) */}
            <div className="flex flex-1 items-center gap-6 lg:gap-8">
              <Link
                href="/home-landing"
                className="flex items-center gap-3 focus-ring rounded-md shrink-0"
                onClick={closeMobileMenu}
              >
                <div className="relative h-200 w-200 sm:h-50 sm:w-50 overflow-hidden rounded-lg bg-transparent flex-shrink-0">
                  <Image
                    src="/assets/Logo-Freshly-Made-4.png"
                    alt="Freshlymade logo"
                    width={100}
                    height={100}
                    className="h-full w-full object-contain"
                    priority
                  />
                </div>
              </Link>

              {/* Desktop Navigation – Home, Menu, About, Gallery, Contact */}
              <div className="hidden items-center space-x-2 lg:flex">
              {navigationItems.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  className={`nav-link-3d cursor-pointer select-none flex items-center space-x-2 rounded-full px-4 py-2.5 text-sm font-medium transition-smooth hover-lift focus-ring ${
                    isActive(item.href)
                      ? 'border-2 border-primary bg-transparent text-foreground'
                      : 'bg-transparent text-foreground hover:border-2 hover:border-primary'
                  }`}
                >
                  <Icon name={item.icon} size={20} />
                  <span className="select-none">{item.label}</span>
                </Link>
              ))}
              </div>

              {/* Order + Notifications (right side) */}
              <div className="hidden items-center space-x-2 lg:ml-auto lg:flex lg:shrink-0">
                <Link
                  href="/order-checkout"
                  className="nav-link-3d cursor-pointer select-none flex items-center space-x-2 rounded-full bg-accent px-6 py-2.5 text-sm font-medium text-accent-foreground shadow-md transition-smooth hover-lift active-press focus-ring hover:shadow-lg relative"
                >
                  <Icon name="ShoppingCartIcon" size={20} />
                  <span className="select-none">Order Now</span>
                  {totalItems > 0 && (
                    <span className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-error text-xs font-bold text-white">
                      {totalItems}
                    </span>
                  )}
                </Link>
                <Link
                  href={notificationsItem.href}
                  className={`flex items-center space-x-2 rounded-lg px-3 py-2.5 text-sm font-medium transition-smooth hover-lift focus-ring ${
                    isActive(notificationsItem.href)
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                  title="Offers & Notifications"
                >
                  <Icon name={notificationsItem.icon} size={20} />
                </Link>
              </div>
            </div>

            {/* Mobile Menu Button */}
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-lg p-2 text-foreground transition-smooth hover:bg-muted focus-ring lg:hidden"
              onClick={toggleMobileMenu}
              aria-expanded={isMobileMenuOpen}
              aria-label="Toggle navigation menu"
            >
              <Icon name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={24} />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-200 bg-background lg:hidden"
          role="dialog"
          aria-modal="true"
        >
          <div className="flex h-full flex-col">
            {/* Mobile Menu Header */}
            <div className="flex h-20 items-center justify-between border-b border-border px-4">
              <Link
                href="/home-landing"
                className="flex items-center gap-3 focus-ring rounded-md shrink-0"
                onClick={closeMobileMenu}
              >
                <div className="relative h-50 w-50 overflow-hidden rounded-lg bg-transparent flex-shrink-0">
                  <Image
                    src="/assets/Logo-Freshly-Made-4.png"
                    alt="Freshlymade logo"
                    width={200}
                    height={200}
                    className="h-full w-full object-contain"
                    priority
                  />
                </div>
                <span className="font-heading text-2xl font-bold text-foreground">Freshlymade</span>
              </Link>
              <button
                type="button"
                className="rounded-lg p-2 text-foreground transition-smooth hover:bg-muted focus-ring"
                onClick={closeMobileMenu}
                aria-label="Close navigation menu"
              >
                <Icon name="XMarkIcon" size={24} />
              </button>
            </div>

            {/* Mobile Menu Items */}
            <div className="flex-1 overflow-y-auto px-4 py-6">
              <div className="space-y-2">
                {navigationItems.map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    className={`flex items-center space-x-3 rounded-lg px-4 py-4 text-base font-medium transition-smooth focus-ring ${
                      isActive(item.href)
                        ? 'bg-primary text-primary-foreground shadow-sm'
                        : 'text-foreground hover:bg-muted'
                    }`}
                    onClick={closeMobileMenu}
                  >
                    <Icon name={item.icon} size={24} />
                    <span>{item.label}</span>
                  </Link>
                ))}

                {/* Mobile Order Button */}
                <Link
                  href="/order-checkout"
                  className="mt-4 flex items-center justify-center space-x-3 rounded-lg bg-accent px-4 py-4 text-base font-medium text-accent-foreground shadow-md transition-smooth active-press focus-ring relative"
                  onClick={closeMobileMenu}
                >
                  <Icon name="ShoppingCartIcon" size={24} />
                  <span>Order Now</span>
                  {totalItems > 0 && (
                    <span className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-error text-xs font-bold text-white">
                      {totalItems}
                    </span>
                  )}
                </Link>

                {/* Mobile Offers & Notifications */}
                <Link
                  href={notificationsItem.href}
                  className={`mt-4 flex items-center space-x-3 rounded-lg px-4 py-4 text-base font-medium transition-smooth focus-ring ${
                    isActive(notificationsItem.href)
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground hover:bg-muted'
                  }`}
                  onClick={closeMobileMenu}
                >
                  <Icon name={notificationsItem.icon} size={24} />
                  <span>Offers & Notifications</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Floating Order Button */}
      <Link
        href="/order-checkout"
        className="fixed bottom-6 right-6 z-200 flex h-14 w-14 items-center justify-center rounded-full bg-accent text-accent-foreground shadow-xl transition-smooth hover-lift active-press focus-ring lg:hidden"
        aria-label="Order now"
      >
        <Icon name="ShoppingCartIcon" size={24} />
        {totalItems > 0 && (
          <span className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full bg-error text-xs font-bold text-white">
            {totalItems}
          </span>
        )}
      </Link>
    </>
  );
};

export default NavigationBar;
